import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Mic, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useCharles } from "@/contexts/CharlesContext";
import { CssOrb } from "@/components/charles/CssOrb";

/**
 * Persistent floating Charles orb.
 * - Always visible on employee pages (mounted in EmployeeLayout).
 * - Hidden on the dedicated /app/employee/charles page (it has its own full UI).
 * - One ElevenLabs session, shared across navigation via CharlesContext.
 */
const POS_KEY = "charles:orbPos";
const ORB_SIZE = 56; // h-14 / w-14
const EDGE = 8;

type Pos = { x: number; y: number };

function clampPos(p: Pos): Pos {
  const maxX = Math.max(EDGE, window.innerWidth - ORB_SIZE - EDGE);
  const maxY = Math.max(EDGE, window.innerHeight - ORB_SIZE - EDGE);
  return {
    x: Math.min(Math.max(p.x, EDGE), maxX),
    y: Math.min(Math.max(p.y, EDGE), maxY),
  };
}

function readStoredPos(): Pos | null {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p?.x !== "number" || typeof p?.y !== "number") return null;
    return clampPos(p);
  } catch {
    return null;
  }
}

export function CharlesOrb() {
  const location = useLocation();
  const onDedicatedPage = location.pathname.startsWith("/app/employee/charles");

  const { session, conversation, configError, started, voiceOptIn, forceTextOnly, start, stop, sendText } =
    useCharles();

  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const panelRef = useRef<HTMLDivElement | null>(null);

  // ---- Drag + persisted position ----------------------------------------
  const [pos, setPos] = useState<Pos | null>(() =>
    typeof window === "undefined" ? null : readStoredPos(),
  );
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{ dx: number; dy: number; moved: boolean } | null>(null);
  const suppressClickRef = useRef(false);

  // Keep the orb on-screen when the window resizes.
  useEffect(() => {
    const onResize = () => setPos((p) => (p ? clampPos(p) : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    dragRef.current = {
      dx: e.clientX - rect.left,
      dy: e.clientY - rect.top,
      moved: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const next = clampPos({ x: e.clientX - d.dx, y: e.clientY - d.dy });
    if (!d.moved) {
      const rect = e.currentTarget.getBoundingClientRect();
      if (Math.abs(next.x - rect.left) < 4 && Math.abs(next.y - rect.top) < 4) return;
      d.moved = true;
      setDragging(true);
      setOpen(false);
    }
    setPos(next);
  };

  const endDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    if (d.moved) {
      suppressClickRef.current = true;
      setDragging(false);
      setPos((p) => {
        if (p) {
          try { localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch { /* ignore */ }
        }
        return p;
      });
    }
  };

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (onDedicatedPage) return null;
  if (session.status === "error" || configError) return null;

  const isReady = session.status === "ready";
  const isConnected = conversation.status === "connected";
  const isSpeaking = conversation.isSpeaking;
  const showVoiceUI = voiceOptIn && !forceTextOnly;

  return (
    <div
      className={cn(
        "fixed z-50 flex flex-col items-end gap-3",
        !pos && "bottom-24 right-6 md:bottom-6",
      )}
      style={pos ? { left: pos.x, top: pos.y } : undefined}
    >

      {open && (
        <div
          ref={panelRef}
          className={cn(
            "w-80 rounded-2xl border border-border bg-card shadow-2xl p-4 space-y-3 animate-in fade-in slide-in-from-bottom-2",
            pos && "absolute bottom-full mb-3",
            pos && (pos.x > window.innerWidth / 2 ? "right-0" : "left-0"),
          )}
        >

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Charles</p>
              <p className="text-xs text-muted-foreground">
                {isConnected
                  ? isSpeaking
                    ? "Speaking…"
                    : "Listening"
                  : started
                    ? "Connecting…"
                    : "Tap start to talk"}
              </p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {!started ? (
            <Button
              onClick={start}
              disabled={!isReady}
              className="w-full"
              size="sm"
            >
              {!isReady ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                  Preparing…
                </>
              ) : showVoiceUI ? (
                <>
                  <Mic className="h-3.5 w-3.5 mr-2" />
                  Start voice
                </>
              ) : (
                "Start chat"
              )}
            </Button>
          ) : (
            <div className="space-y-2">
              {!showVoiceUI && (
                <div className="flex gap-2">
                  <Input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Say what's on your mind…"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && text.trim()) {
                        sendText(text);
                        setText("");
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      sendText(text);
                      setText("");
                    }}
                  >
                    Send
                  </Button>
                </div>
              )}
              <Button variant="outline" size="sm" className="w-full" onClick={stop}>
                End conversation
              </Button>
            </div>
          )}
        </div>
      )}

      <button
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClick={() => {
          if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return;
          }
          setOpen((v) => !v);
        }}
        aria-label="Talk to Charles (drag to move)"
        className={cn(
          "relative h-14 w-14 rounded-full transition-transform touch-none select-none",
          dragging ? "cursor-grabbing scale-105" : "cursor-grab hover:scale-105 active:scale-95",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background",
          isConnected ? "focus:ring-emerald-400" : "focus:ring-orange-400",
        )}
      >

        <CssOrb fill className="absolute inset-0" />
        {/* Idle breathing ring when connected but quiet */}
        {isConnected && !isSpeaking && (
          <span
            aria-hidden
            className="absolute -inset-1 rounded-full opacity-70"
            style={{
              boxShadow: "0 0 0 2px rgba(52, 211, 153, 0.45)",
              animation: "pulse 2.4s ease-in-out infinite",
            }}
          />
        )}
      </button>

    </div>
  );
}
